import * as React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';
import { ChevronRight, ChevronLeft } from 'react-feather';
import { Image } from '../generated/graphql';
import { imageSize } from '../utils/imagekit';
import { MatchPhotoOverlayMenu } from '../pages/Match/PostMatch/MatchPhotoOverlayMenu';
import { FeatherIcon } from './FeatherIcon';

export type GalleryImage = Pick<Image, 'id' | 'url' | 'caption'>;

interface Props {
  images: GalleryImage[];
  selectedImageIdx: O.Option<number>;
  selectImageIdx: React.Dispatch<React.SetStateAction<O.Option<number>>>;

  isAdmin: boolean;
  deleteImage?: (id: string) => Promise<void>;
  setHighlight?: (id: string) => Promise<void>;
}

const overlayButtonStyle =
  'text-white p-2 rounded-full bg-grey-800 bg-opacity-30 flex items-center justify-center focus:outline-none';

const navButtonStyle =
  'flex absolute text-white w-52 max-w-[33%] h-52 max-h-[50%] py-32 inset-y-1/2 -translate-y-1/2 items-center p-6 opacity-40';

export const PhotoOverlay: React.FC<Props> = ({
  images,
  selectedImageIdx,
  selectImageIdx,
  isAdmin,
  deleteImage,
  setHighlight,
}) => {
  const selectedImage = pipe(
    selectedImageIdx,
    O.chain((i) => A.lookup(i)(images)),
  );
  const show = O.isSome(selectedImage);

  const selectNextImage = () =>
    selectImageIdx((currentIdx) =>
      pipe(
        currentIdx,
        O.map((i) => (i + 1) % images.length),
      ),
    );

  const selectPrevImage = () =>
    selectImageIdx((currentIdx) =>
      pipe(
        currentIdx,
        O.map((i) => (i + images.length - 1) % images.length),
      ),
    );

  const onClose = () => selectImageIdx(O.none);

  return (
    <Transition appear show={show} as={React.Fragment}>
      <Dialog
        onClose={onClose}
        as="div"
        className="fixed inset-0 z-20 overflow-y-auto flex justify-center items-center py-16"
      >
        <Dialog.Overlay className="fixed inset-0 bg-grey-700 bg-opacity-60 backdrop-filter backdrop-blur-sm" />

        <div className="inline-block w-full sm:w-4/5 h-full overflow-hidden text-left align-middle transition-all transform bg-grey-900 shadow-xl sm:rounded-lg relative">
          <div className="absolute right-4 top-4 flex items-center space-x-1 z-10">
            {pipe(
              selectedImage,
              O.filter(() => isAdmin),
              O.chain((i) =>
                deleteImage && setHighlight
                  ? O.some(
                      <MatchPhotoOverlayMenu
                        imageId={i.id}
                        key={i.id}
                        deleteImage={deleteImage}
                        setHighlight={setHighlight}
                      />,
                    )
                  : O.none,
              ),
              O.toNullable,
            )}

            <button
              type="button"
              onClick={onClose}
              className={overlayButtonStyle}
            >
              <FeatherIcon icon="x" aria-label="close" />
            </button>
          </div>

          {images.length > 1 ? (
            <>
              <button
                onClick={selectNextImage}
                type="button"
                className={`${navButtonStyle} right-0 justify-end`}
              >
                <ChevronRight className="h-14 w-14" />
              </button>

              <button
                onClick={selectPrevImage}
                type="button"
                className={`${navButtonStyle} left-0 justify-start`}
              >
                <ChevronLeft className="h-14 w-14" />
              </button>
            </>
          ) : null}

          {pipe(
            selectedImage,
            O.map((i) => (
              <img
                key={i.id}
                src={imageSize(i.url, 'full')}
                className="object-contain w-full h-full"
                alt={i.caption || undefined}
              />
            )),
            O.toNullable,
          )}
        </div>
      </Dialog>
    </Transition>
  );
};
